import { useEffect, useRef } from 'react'

import { useTrackEvent } from '@/hooks/useTrackEvent'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'

import type { Cart } from '@/gen/ecom/models'

interface SeenCart {
  code?: string
  contentKey: string
}

const getCartContentKey = ({ code, entries }: Cart) =>
  JSON.stringify([code, entries?.map(({ product, quantity }) => [product?.code, quantity])])

const NO_CART: SeenCart = { contentKey: getCartContentKey({}) }

const isFreshCartAfterOrder = (previous: SeenCart, cart: Cart) => previous.code !== cart.code && !cart.entries?.length

const isCartChange = (previous: SeenCart | null, current: SeenCart, cart: Cart) =>
  previous !== null && previous.contentKey !== current.contentKey && !isFreshCartAfterOrder(previous, cart)

/** Emits the complete cart whenever its contents change. `null` means the visitor has no cart, so the cart the
 * first add creates is a change; `undefined` means the cart is not known yet. The first cart seen after mount is a
 * page load, not a change, and the empty cart that replaces an ordered one is skipped: the order closes the old cart. */
export const useCartChangeTracking = (cart: Cart | null | undefined) => {
  const { track } = useTrackEvent()
  const lastSeenCartRef = useRef<SeenCart | null>(null)

  useEffect(() => {
    if (cart === undefined) {
      return
    }

    const previous = lastSeenCartRef.current
    const current = cart ? { code: cart.code, contentKey: getCartContentKey(cart) } : NO_CART
    lastSeenCartRef.current = current

    if (cart && isCartChange(previous, current, cart)) {
      track(TrackingTopic.CartUpdated, { cart })
    }
  }, [cart, track])
}
